// Dayjs
import dayjs from 'dayjs'

// Mantine
import {
    Badge,
    Anchor,
    Tooltip,
    ActionIcon,
    HoverCard,
    Menu,
} from '@mantine/core'

// Icons
import {
    IconPrinter,
    IconCopy,
    IconFileTypePdf,
    IconDotsVertical,
    IconExternalLink,
} from '@tabler/icons-react'

// Hooks
import useUserLocation from '@/hooks/useUserLocation'

// Classnames
import classNames from 'classnames'

// Documents clients
import { DOCUMENTS_URL } from '@/lib/constants'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

import { statuses, serviceCodes } from '@/lib/shipment'

const getNameFromLocation = (location) => {
    if (location?.name) {
        return location.name
    }

    return 'Client'
}

const FileDisplay = ({ name, url = '' }) => {
    return (
        <a
            href={url}
            target="_blank"
            className="flex flex-col items-center justify-center gap-2 border px-2 py-4 rounded-xl text-gray-800 text-sm text-center hover:bg-gray-50 cursor-pointer"
        >
            <span className="text-sm">{name}</span>
        </a>
    )
}

const AddressDisplay = ({ address, label }) => {
    return (
        <div className="flex flex-col">
            <span className="text-sm text-gray-500">{label}</span>

            <div className="flex flex-col my-2">
                <span>
                    {[address.name, address.company]
                        .filter(Boolean)
                        .join(' - ')}
                </span>
                <span>{address.street1}</span>
                <span>{address.street2}</span>
                <span>
                    {[address.zip, address.city, address.state]
                        .filter(Boolean)
                        .join(' - ')}
                </span>

                <span>{address.country}</span>
            </div>

            <div className="flex flex-col text-gray-500 text-sm mt-auto">
                <span>{address.email}</span>

                <span>{address.phone}</span>
            </div>
        </div>
    )
}

export default function Shipment({
    shipment,
    reloadShipments = null,
    selectedRole = null, // 'from_customer' | 'to_customer' | null
}) {
    const status = statuses[shipment.tracking_status] ?? statuses.unknown

    const service = serviceCodes[shipment.service] ?? null

    const verbose_locations = `${getNameFromLocation(
        shipment.from_location
    )} -> ${getNameFromLocation(shipment.to_location)}`

    const { userPrinter, userPrinters } = useUserLocation()
    const a4Printer = userPrinters.a4

    const tracking_url = `https://www.dhl.com/global-en/home/tracking/tracking-express.html?tracking-id=${shipment.tracking_code}&submit=1`

    // Même URL que celle qu'un client recevra : c'est celle-là qu'on copie
    // pour la transmettre, donc c'est celle-là qu'il faut avoir sous la main.
    const public_label_url = `${DOCUMENTS_URL}/${shipment.public_token}/shipping-label.pdf`

    const { requestPost } = useAxiosApi()

    const printShipment = async () => {
        await requestPost(`/shipments/${shipment.id}/print`, {
            printnode_ids: userPrinter.printnode_ids,
        })
    }

    const printCommercialInvoice = async () => {
        await requestPost(`/shipments/${shipment.id}/print-commercial-invoice`, {
            printnode_ids: a4Printer.printnode_ids,
        })
    }

    const isCancelled = !!shipment.canceled_at
    const isSelected = !!selectedRole

    const selectedBadgeLabel = selectedRole === 'from_customer'
        ? 'Sera utilisé par le client'
        : selectedRole === 'to_customer'
            ? 'Sera utilisé par l\'atelier'
            : null

    return (
        <div className={classNames(
            'flex flex-col border p-4 rounded-xl bg-white',
            isCancelled
                ? 'border-gray-200 opacity-50'
                : isSelected
                    ? 'border-green-400 bg-green-50/30'
                    : 'border-yellow-500/40 bg-yellow-50/30'
        )}>
            <div className="flex justify-between items-center mb-2">
                <HoverCard withArrow>
                    <HoverCard.Target>
                        <span className="text-lg font-mediu">
                            {verbose_locations}
                        </span>
                    </HoverCard.Target>

                    <HoverCard.Dropdown>
                        <div className="grid grid-cols-2 gap-2">
                            <AddressDisplay
                                address={shipment.from_address}
                                label="Expéditeur"
                            />

                            <AddressDisplay
                                address={shipment.to_address}
                                label="Destinataire"
                            />
                        </div>
                    </HoverCard.Dropdown>
                </HoverCard>

                <div className="flex items-center gap-2">
                    {isCancelled && (
                        <Badge color="red" variant="light">Annulé</Badge>
                    )}

                    {!isCancelled && selectedBadgeLabel && (
                        <Badge color="green" variant="light">{selectedBadgeLabel}</Badge>
                    )}

                    {shipment.is_used && (
                        <Badge color="darkgray">Utilisé</Badge>
                    )}

                    <Menu position="bottom-end">
                        <Menu.Target>
                            <ActionIcon variant="default" size="md">
                                <IconDotsVertical size={16} />
                            </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown miw={300}>
                            <Menu.Label>Fichiers</Menu.Label>

                            <a href={shipment.label_url} target="_blank">
                                <Menu.Item
                                    href={shipment.label_url}
                                    target="_blank"
                                    rightSection={<IconFileTypePdf size={16} />}
                                >
                                    Ouvrir le bordereau thermique
                                </Menu.Item>
                            </a>

                            <a href={public_label_url} target="_blank">
                                <Menu.Item
                                    rightSection={<IconFileTypePdf size={16} />}
                                >
                                    Ouvrir le bordereau A4
                                </Menu.Item>
                            </a>

                            {shipment.commercial_invoice && (
                                <a
                                    href={shipment.commercial_invoice}
                                    target="_blank"
                                >
                                    <Menu.Item
                                        rightSection={
                                            <IconFileTypePdf size={16} />
                                        }
                                    >
                                        Ouvrir la facture commerciale
                                    </Menu.Item>
                                </a>
                            )}

                            <Menu.Divider />

                            <Menu.Label>Suivi</Menu.Label>

                            <a href={tracking_url} target="_blank">
                                <Menu.Item
                                    rightSection={
                                        <IconExternalLink size={16} />
                                    }
                                >
                                    Voir le suivi DHL
                                </Menu.Item>
                            </a>

                            <Menu.Item rightSection={<IconCopy size={16} />}>
                                Copier le numéro de suivi
                            </Menu.Item>

                            {(userPrinter || (a4Printer && shipment.commercial_invoice)) && (
                                <>
                                    <Menu.Divider />

                                    <Menu.Label>Impression</Menu.Label>

                                    {userPrinter && (
                                        <Menu.Item
                                            onClick={printShipment}
                                            rightSection={<IconPrinter size={16} />}
                                        >
                                            Imprimer sur {userPrinter.name}
                                        </Menu.Item>
                                    )}

                                    {a4Printer && shipment.commercial_invoice && (
                                        <Menu.Item
                                            onClick={printCommercialInvoice}
                                            rightSection={<IconPrinter size={16} />}
                                        >
                                            Facture commerciale sur {a4Printer.name}
                                        </Menu.Item>
                                    )}
                                </>
                            )}
                        </Menu.Dropdown>
                    </Menu>
                </div>
            </div>

            <div className="mb-2 flex gap-2">
                <Anchor href={tracking_url} size="sm" target="_blank">
                    {shipment.tracking_code}
                </Anchor>

                <Tooltip label={dayjs(shipment.created_at).format('llll')}>
                    <span className="text-sm text-gray-400">
                        - Créé {dayjs(shipment.created_at).calendar()}
                    </span>
                </Tooltip>
            </div>

            <div className="flex justify-between gap-4">
                <div className="flex flex-col items-start justify-end min-w-0">
                    <span
                        className={classNames(
                            status.textColor,
                            'font-medium mb-1'
                        )}
                    >
                        {status.label}
                    </span>

                    {shipment.last_tracker_message && (
                        <span className="text-gray-500 flex gap-1 text-sm max-w-full">
                            <span className="truncate">
                                {shipment.last_tracker_message}
                            </span>

                            <span className="shrink-0">
                                (
                                {dayjs(shipment.last_tracker_update_at).format(
                                    'll'
                                )}
                                )
                            </span>
                        </span>
                    )}

                    {shipment.est_delivery_date &&
                        shipment.tracking_status !== 'delivered' &&
                        false && (
                            <span className="text-gray-500 text-sm">
                                Livraison estimée :{' '}
                                {dayjs(shipment.est_delivery_date).format('ll')}
                            </span>
                        )}

                    {shipment.tracking_status === 'delivered' &&
                        shipment.delivered_at && (
                            <span className="text-green-500 text-sm">
                                Livré le{' '}
                                {dayjs(shipment.delivered_at).format('ll')}
                            </span>
                        )}
                </div>

                <div className="flex flex-col text-gray-400 text-sm text-right">
                    <span>{shipment.number_of_packages} colis</span>

                    <span>Incoterm {shipment.incoterm ?? 'inconnu'}</span>
                    <span>Service {service?.code ?? 'inconnu'}</span>
                    <span>{shipment.paperless ? 'Paperless' : 'Sans paperless'}</span>
                </div>
            </div>

            <div className="my-4 grid grid-cols-3 gap-2 hidden">
                <FileDisplay
                    name="Bordereau thermique"
                    url={shipment.label_url}
                />

                <FileDisplay name="Bordereau A4" url={shipment.label_url} />

                <FileDisplay
                    name="Facture commerciale"
                    url={shipment.invoice_url}
                />
            </div>

            <pre className="hidden">{JSON.stringify(shipment, null, 2)}</pre>
        </div>
    )
}
